// banking/src/pages/onboarding/corporate/steps/Step4_Representatives.tsx

import React from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData } from '../CorporateOnboarding';
import RepresentativeCard from '../components/RepresentativeCard';
import { getCurrentApiUrl } from '../../../../config/server.config';

interface Step4Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
  representativesFinalized: boolean;
  setRepresentativesFinalized: (value: boolean) => void;
}

const Step4_Representatives: React.FC<Step4Props> = ({
  formData,
  updateFormData,
  language,
  errors,
  representativesFinalized,
  setRepresentativesFinalized,
}) => {
  // Check if a representative has all required documents uploaded
  const isRepresentativeComplete = (rep: any): boolean => {
    if (!rep.documents) return false;

    // Common required documents
    const hasCommonDocs =
      !!rep.documents.selfieWithId &&
      !!rep.documents.proofOfAddress &&
      !!rep.documents.bankStatement;

    // Check ID type specific documents
    if (rep.documentType === 'ID_CARD') {
      return hasCommonDocs &&
        !!rep.documents.idCardFront &&
        !!rep.documents.idCardBack;
    } else if (rep.documentType === 'DRIVING_LICENSE') {
      return hasCommonDocs &&
        !!rep.documents.drivingLicenseFront &&
        !!rep.documents.drivingLicenseBack;
    } else if (rep.documentType === 'PASSPORT') {
      return hasCommonDocs && !!rep.documents.passport;
    }

    return false;
  };

  // Check if at least one representative is complete
  const hasAtLeastOneCompleteRep = (): boolean => {
    return formData.representatives.some(rep => isRepresentativeComplete(rep));
  };

  // Check if at least one ultimate beneficiary exists
  const hasUltimateBeneficiary = (): boolean => {
    return formData.representatives.some(rep => rep.isUltimateBeneficiary === true);
  };

  // Calculate total ownership percentage
  const getTotalOwnership = (): number => {
    return formData.representatives.reduce(
      (sum, rep) => sum + (rep.ownershipPercentage || 0),
      0
    );
  };

  // Get validation errors
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];

    if (!hasAtLeastOneCompleteRep()) {
      errors.push('missing_complete_rep');
    }

    if (!hasUltimateBeneficiary()) {
      errors.push('missing_ultimate_beneficiary');
    }

    const totalOwnership = getTotalOwnership();
    if (totalOwnership < 51) {
      errors.push('insufficient_ownership');
    }

    return errors;
  };

  // Check if can finalize (all validations must pass)
  const canFinalize = (): boolean => {
    return (
      formData.representatives.length > 0 &&
      hasAtLeastOneCompleteRep() &&
      hasUltimateBeneficiary() &&
      getTotalOwnership() >= 51
    );
  };

  const handleAddRepresentative = () => {
    // Set index to create new representative
    updateFormData({ currentRepresentativeIndex: formData.representatives.length });
  };

  const handleEditRepresentative = (index: number) => {
    // Set index to edit existing representative
    updateFormData({ currentRepresentativeIndex: index });
  };

  const handleDeleteRepresentative = async (index: number) => {
    const representative = formData.representatives[index];
    const representativeId = representative?.id;

    // If representative has ID, delete from database
    if (representativeId) {
      const confirmed = window.confirm(
        language === 'es'
          ? '¿Está seguro de que desea eliminar este representante? Esta acción no se puede deshacer y eliminará todos los documentos asociados.'
          : language === 'fr'
          ? 'Êtes-vous sûr de vouloir supprimer ce représentant ? Cette action est irréversible et supprimera tous les documents associés.'
          : language === 'de'
          ? 'Sind Sie sicher, dass Sie diesen Vertreter löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden und löscht alle zugehörigen Dokumente.'
          : language === 'it'
          ? 'Sei sicuro di voler eliminare questo rappresentante? Questa azione è irreversibile ed eliminerà tutti i documenti associati.'
          : language === 'pt'
          ? 'Tem certeza de que deseja excluir este representante? Esta ação é irreversível e excluirá todos os documentos associados.'
          : language === 'nl'
          ? 'Weet u zeker dat u deze vertegenwoordiger wilt verwijderen? Deze actie is onomkeerbaar en verwijdert alle bijbehorende documenten.'
          : language === 'ru'
          ? 'Вы уверены, что хотите удалить этого представителя? Это действие необратимо и удалит все связанные документы.'
          : language === 'ar'
          ? 'هل أنت متأكد من أنك تريد حذف هذا الممثل؟ هذا الإجراء لا رجعة فيه وسيحذف جميع المستندات المرتبطة به.'
          : 'Are you sure you want to delete this representative? This action cannot be undone and will delete all associated documents.'
      );

      if (!confirmed) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${getCurrentApiUrl()}/onboarding/corporate/representative/${representativeId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete representative');
        }

        // Remove from local state after successful deletion
        const newRepresentatives = formData.representatives.filter((_, i) => i !== index);
        updateFormData({ representatives: newRepresentatives });

        alert(
          language === 'es'
            ? 'Representante eliminado correctamente'
            : language === 'fr'
            ? 'Représentant supprimé avec succès'
            : language === 'de'
            ? 'Vertreter erfolgreich gelöscht'
            : language === 'it'
            ? 'Rappresentante eliminato con successo'
            : language === 'pt'
            ? 'Representante excluído com sucesso'
            : language === 'nl'
            ? 'Vertegenwoordiger succesvol verwijderd'
            : language === 'ru'
            ? 'Представитель успешно удален'
            : language === 'ar'
            ? 'تم حذف الممثل بنجاح'
            : 'Representative deleted successfully'
        );
      } catch (error) {
        console.error('Error deleting representative:', error);
        alert(
          language === 'es'
            ? 'Error al eliminar el representante'
            : language === 'fr'
            ? 'Erreur lors de la suppression du représentant'
            : language === 'de'
            ? 'Fehler beim Löschen des Vertreters'
            : language === 'it'
            ? 'Errore durante l\'eliminazione del rappresentante'
            : language === 'pt'
            ? 'Erro ao excluir o representante'
            : language === 'nl'
            ? 'Fout bij het verwijderen van de vertegenwoordiger'
            : language === 'ru'
            ? 'Ошибка при удалении представителя'
            : language === 'ar'
            ? 'خطأ في حذف الممثل'
            : 'Error deleting representative'
        );
      }
    } else {
      // No ID means not saved yet, just remove from local state
      const newRepresentatives = formData.representatives.filter((_, i) => i !== index);
      updateFormData({ representatives: newRepresentatives });
    }
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step4_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {getTranslation('step4_subtitle', language)}
        </p>
      </div>

      {/* Info box */}
      <div className="bg-[#F5F3E8] border border-[#E5DDB8] rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-[#B3A369]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-[#9A8B57]">
              {language === 'es'
                ? 'Debe agregar al menos un representante legal de la empresa. Los representantes pueden ser directores, accionistas o beneficiarios finales.'
                : language === 'fr'
                ? 'Vous devez ajouter au moins un représentant légal de l\'entreprise. Les représentants peuvent être des directeurs, actionnaires ou bénéficiaires effectifs.'
                : language === 'de'
                ? 'Sie müssen mindestens einen gesetzlichen Vertreter des Unternehmens hinzufügen. Vertreter können Direktoren, Aktionäre oder wirtschaftlich Berechtigte sein.'
                : language === 'it'
                ? 'Devi aggiungere almeno un rappresentante legale dell\'azienda. I rappresentanti possono essere direttori, azionisti o beneficiari finali.'
                : language === 'pt'
                ? 'Você deve adicionar pelo menos um representante legal da empresa. Os representantes podem ser diretores, acionistas ou beneficiários finais.'
                : language === 'nl'
                ? 'U moet ten minste één wettelijk vertegenwoordiger van het bedrijf toevoegen. Vertegenwoordigers kunnen directeuren, aandeelhouders of uiteindelijk belanghebbenden zijn.'
                : language === 'ru'
                ? 'Вы должны добавить хотя бы одного законного представителя компании. Представители могут быть директорами, акционерами или конечными бенефициарами.'
                : language === 'ar'
                ? 'يجب عليك إضافة ممثل قانوني واحد على الأقل للشركة. يمكن أن يكون الممثلون مديرين أو مساهمين أو مستفيدين نهائيين.'
                : 'You must add at least one legal representative of the company. Representatives can be directors, shareholders, or ultimate beneficiaries.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Representatives List */}
      {formData.representatives.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            {getTranslation('representatives_added', language)} ({formData.representatives.length})
          </h3>

          {formData.representatives.map((rep, index) => (
            <RepresentativeCard
              key={index}
              representative={rep}
              index={index}
              language={language}
              onEdit={() => handleEditRepresentative(index)}
              onDelete={() => handleDeleteRepresentative(index)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {language === 'es'
              ? 'No hay representantes agregados'
              : language === 'fr'
              ? 'Aucun représentant ajouté'
              : language === 'de'
              ? 'Keine Vertreter hinzugefügt'
              : language === 'it'
              ? 'Nessun rappresentante aggiunto'
              : language === 'pt'
              ? 'Nenhum representante adicionado'
              : language === 'nl'
              ? 'Geen vertegenwoordigers toegevoegd'
              : language === 'ru'
              ? 'Представители не добавлены'
              : language === 'ar'
              ? 'لم يتم إضافة ممثلين'
              : 'No representatives added'
            }
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {language === 'es'
              ? 'Comience agregando un representante legal de la empresa'
              : language === 'fr'
              ? 'Commencez par ajouter un représentant légal de l\'entreprise'
              : language === 'de'
              ? 'Beginnen Sie mit dem Hinzufügen eines gesetzlichen Vertreters des Unternehmens'
              : language === 'it'
              ? 'Inizia aggiungendo un rappresentante legale dell\'azienda'
              : language === 'pt'
              ? 'Comece adicionando um representante legal da empresa'
              : language === 'nl'
              ? 'Begin met het toevoegen van een wettelijk vertegenwoordiger van het bedrijf'
              : language === 'ru'
              ? 'Начните с добавления законного представителя компании'
              : language === 'ar'
              ? 'ابدأ بإضافة ممثل قانوني للشركة'
              : 'Start by adding a legal representative of the company'
            }
          </p>
        </div>
      )}

      {/* Add Representative and Finalize Buttons */}
      <div className="flex justify-center gap-4">
        <button
          type="button"
          onClick={handleAddRepresentative}
          disabled={representativesFinalized}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B3A369] ${
            representativesFinalized
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'text-white bg-[#B3A369] hover:bg-[#9A8B57]'
          }`}
        >
          <svg
            className="-ml-1 mr-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          {getTranslation('add_representative_button', language)}
        </button>

        {formData.representatives.length > 0 && !representativesFinalized && (
          <button
            type="button"
            onClick={() => setRepresentativesFinalized(true)}
            disabled={!canFinalize()}
            className={`inline-flex items-center px-6 py-3 border-2 text-base font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B3A369] ${
              canFinalize()
                ? 'border-[#B3A369] text-[#B3A369] bg-white hover:bg-[#F5F3E8]'
                : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
            }`}
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            {getTranslation('finalize_representatives_button', language)}
          </button>
        )}
      </div>

      {/* Warning if cannot finalize - show specific validation errors */}
      {formData.representatives.length > 0 && !canFinalize() && !representativesFinalized && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {language === 'es'
                  ? 'No se puede finalizar aún'
                  : language === 'fr'
                  ? 'Impossible de finaliser'
                  : language === 'de'
                  ? 'Kann noch nicht abgeschlossen werden'
                  : language === 'it'
                  ? 'Impossibile finalizzare'
                  : language === 'pt'
                  ? 'Não é possível finalizar'
                  : language === 'nl'
                  ? 'Kan nog niet worden afgerond'
                  : language === 'ru'
                  ? 'Невозможно завершить'
                  : language === 'ar'
                  ? 'لا يمكن الإنهاء بعد'
                  : 'Cannot finalize yet'
                }
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc list-inside space-y-1">
                  {getValidationErrors().map((error, index) => (
                    <li key={index}>
                      {error === 'missing_complete_rep' && (
                        language === 'es'
                          ? 'Al menos un representante debe tener TODOS los documentos subidos'
                          : language === 'fr'
                          ? 'Au moins un représentant doit avoir TOUS les documents téléchargés'
                          : language === 'de'
                          ? 'Mindestens ein Vertreter muss ALLE Dokumente hochgeladen haben'
                          : language === 'it'
                          ? 'Almeno un rappresentante deve avere TUTTI i documenti caricati'
                          : language === 'pt'
                          ? 'Pelo menos um representante deve ter TODOS os documentos carregados'
                          : language === 'nl'
                          ? 'Ten minste één vertegenwoordiger moet ALLE documenten hebben geüpload'
                          : language === 'ru'
                          ? 'Хотя бы один представитель должен иметь ВСЕ загруженные документы'
                          : language === 'ar'
                          ? 'يجب أن يكون لدى ممثل واحد على الأقل جميع المستندات المحملة'
                          : 'At least one representative must have ALL documents uploaded'
                      )}
                      {error === 'missing_ultimate_beneficiary' && (
                        language === 'es'
                          ? 'Debe haber al menos un beneficiario final (Ultimate Beneficiary)'
                          : language === 'fr'
                          ? 'Il doit y avoir au moins un bénéficiaire effectif (Ultimate Beneficiary)'
                          : language === 'de'
                          ? 'Es muss mindestens einen wirtschaftlich Berechtigten (Ultimate Beneficiary) geben'
                          : language === 'it'
                          ? 'Deve esserci almeno un beneficiario effettivo (Ultimate Beneficiary)'
                          : language === 'pt'
                          ? 'Deve haver pelo menos um beneficiário final (Ultimate Beneficiary)'
                          : language === 'nl'
                          ? 'Er moet ten minste één uiteindelijke begunstigde (Ultimate Beneficiary) zijn'
                          : language === 'ru'
                          ? 'Должен быть хотя бы один конечный бенефициар (Ultimate Beneficiary)'
                          : language === 'ar'
                          ? 'يجب أن يكون هناك مستفيد نهائي واحد على الأقل (Ultimate Beneficiary)'
                          : 'There must be at least one Ultimate Beneficiary'
                      )}
                      {error === 'insufficient_ownership' && (
                        language === 'es'
                          ? `La suma de porcentajes de propiedad debe ser al menos 51% (actual: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'fr'
                          ? `La somme des pourcentages de propriété doit être au moins 51% (actuel: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'de'
                          ? `Die Summe der Eigentumsanteile muss mindestens 51% betragen (aktuell: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'it'
                          ? `La somma delle percentuali di proprietà deve essere almeno del 51% (attuale: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'pt'
                          ? `A soma das porcentagens de propriedade deve ser pelo menos 51% (atual: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'nl'
                          ? `De som van de eigendomspercentages moet ten minste 51% zijn (huidig: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'ru'
                          ? `Сумма процентов владения должна быть не менее 51% (текущая: ${getTotalOwnership().toFixed(1)}%)`
                          : language === 'ar'
                          ? `يجب أن يكون مجموع نسب الملكية 51٪ على الأقل (الحالي: ${getTotalOwnership().toFixed(1)}٪)`
                          : `The sum of ownership percentages must be at least 51% (current: ${getTotalOwnership().toFixed(1)}%)`
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        {representativesFinalized && (
          <button
            type="button"
            onClick={() => setRepresentativesFinalized(false)}
            className="inline-flex items-center px-6 py-3 border-2 border-gray-400 text-base font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                clipRule="evenodd"
              />
            </svg>
            {getTranslation('edit_representatives_button', language)}
          </button>
        )}
      </div>

      {/* Error message */}
      {errors.representatives && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{errors.representatives}</p>
            </div>
          </div>
        </div>
      )}

      {/* Ownership percentage validation (optional check) */}
      {formData.representatives.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {language === 'es'
                ? 'Porcentaje total de propiedad:'
                : language === 'fr'
                ? 'Pourcentage total de propriété :'
                : language === 'de'
                ? 'Gesamter Eigentumsanteil:'
                : language === 'it'
                ? 'Percentuale totale di proprietà:'
                : language === 'pt'
                ? 'Porcentagem total de propriedade:'
                : language === 'nl'
                ? 'Totaal eigendomspercentage:'
                : language === 'ru'
                ? 'Общая доля владения:'
                : language === 'ar'
                ? 'نسبة الملكية الإجمالية:'
                : 'Total ownership percentage:'
              }
            </span>
            <span className={`text-lg font-bold ${
              formData.representatives.reduce((sum, rep) => sum + rep.ownershipPercentage, 0) >= 51
                ? 'text-green-600'
                : 'text-yellow-600'
            }`}>
              {formData.representatives.reduce((sum, rep) => sum + rep.ownershipPercentage, 0)}%
            </span>
          </div>
          {formData.representatives.reduce((sum, rep) => sum + rep.ownershipPercentage, 0) < 51 && (
            <p className="mt-2 text-xs text-yellow-700">
              {language === 'es'
                ? 'Nota: Para cuentas corporativas, BELOBABA requiere que al menos uno de los accionistas posea ≥51% de la empresa.'
                : language === 'fr'
                ? 'Remarque : Pour les comptes d\'entreprise, BELOBABA exige qu\'au moins un des actionnaires possède ≥51% de l\'entreprise.'
                : language === 'de'
                ? 'Hinweis: Für Unternehmenskonten verlangt BELOBABA, dass mindestens einer der Aktionäre ≥51% des Unternehmens besitzt.'
                : language === 'it'
                ? 'Nota: Per gli account aziendali, BELOBABA richiede che almeno uno degli azionisti possieda ≥51% dell\'azienda.'
                : language === 'pt'
                ? 'Nota: Para contas corporativas, BELOBABA exige que pelo menos um dos acionistas possua ≥51% da empresa.'
                : language === 'nl'
                ? 'Opmerking: Voor bedrijfsaccounts vereist BELOBABA dat ten minste één van de aandeelhouders ≥51% van het bedrijf bezit.'
                : language === 'ru'
                ? 'Примечание: Для корпоративных счетов BELOBABA требует, чтобы хотя бы один из акционеров владел ≥51% компании.'
                : language === 'ar'
                ? 'ملاحظة: بالنسبة لحسابات الشركات، يتطلب BELOBABA أن يمتلك أحد المساهمين على الأقل ≥51٪ من الشركة.'
                : 'Note: For corporate accounts, BELOBABA requires that at least one shareholder owns ≥51% of the company.'
              }
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Step4_Representatives;
